import { useRateAskHelenaMessage } from "controller";
import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import toast from "react-hot-toast";
import type { AskHelenaFeedbackRating, AskHelenaMessageFeedback } from "shared";
import { getApiErrorMessage } from "utilz/helpers";
import { askHelenaMessages, setAskHelenaMessageFeedback } from "../store";

export type AskHelenaRateMessage = (
  messageId: string,
  rating: AskHelenaFeedbackRating,
) => void;

export const useAskHelenaMessageFeedback = (): AskHelenaRateMessage => {
  const setFeedback = useSetAtom(setAskHelenaMessageFeedback);
  const { mutateAsync: rateMessage } = useRateAskHelenaMessage();

  const readFeedback = useAtomCallback(
    useCallback(
      (get, _set, messageId: string): AskHelenaMessageFeedback | null =>
        get(askHelenaMessages).find(
          (message) => message.serverMessageId === messageId,
        )?.feedback ?? null,
      [],
    ),
  );

  return useCallback(
    (messageId: string, rating: AskHelenaFeedbackRating) => {
      const previous = readFeedback(messageId);
      if (previous?.rating === rating) return;

      setFeedback({ messageId, feedback: { rating } });

      void (async () => {
        try {
          await rateMessage({ messageId, rating });
        } catch (error: unknown) {
          setFeedback({ messageId, feedback: previous });
          toast.error(
            getApiErrorMessage(error, "Couldn't save that feedback."),
          );
        }
      })();
    },
    [rateMessage, readFeedback, setFeedback],
  );
};
